import { 
  DashboardData, 
  Message, 
  VersionInfo, 
  TestFlightTester, 
  TestFlightStats,
  BulkUpdateRequest,
  TestFlightInviteRequest,
  ApiResponse
} from '../types';

// Base API service for all admin panel API calls
class ApiService {
  private baseUrl = '/api';

  private async request<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const config: RequestInit = {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        if (response.status === 401) {
          // Redirect to login on unauthorized
          window.location.href = '/';
          throw new Error('Unauthorized');
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Dashboard data
  async getDashboardData(): Promise<DashboardData> {
    return this.request<DashboardData>('/signups');
  }

  // Beta signup management
  async updateSignupStatus(signupId: string, status: string): Promise<void> {
    await this.request('/update-status', {
      method: 'POST',
      body: JSON.stringify({ signupId, status }),
    });
  }

  async bulkUpdateStatus(request: BulkUpdateRequest): Promise<void> {
    await this.request('/bulk-update-status', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Messages management
  async getMessages(): Promise<{ messages: Message[] }> {
    return this.request<{ messages: Message[] }>('/messages');
  }

  async createMessage(message: Omit<Message, 'id' | 'createdAt'>): Promise<Message> {
    return this.request<Message>('/messages', {
      method: 'POST',
      body: JSON.stringify(message),
    });
  }

  async updateMessage(id: string, message: Partial<Message>): Promise<Message> {
    return this.request<Message>(`/messages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(message),
    });
  }

  async deleteMessage(id: string): Promise<void> {
    await this.request(`/messages/${id}`, {
      method: 'DELETE',
    });
  }

  // Version management
  async getVersionInfo(): Promise<VersionInfo> {
    return this.request<VersionInfo>('/version-check');
  }

  async updateVersion(version: Partial<VersionInfo>): Promise<ApiResponse<void>> {
    return this.request<ApiResponse<void>>('/version-update', {
      method: 'POST',
      body: JSON.stringify(version),
    });
  }

  // TestFlight management
  async getTestFlightTesters(): Promise<{ testers: TestFlightTester[] }> {
    return this.request<{ testers: TestFlightTester[] }>('/testflight/testers');
  }

  async getTestFlightStats(): Promise<{ stats: TestFlightStats }> {
    return this.request<{ stats: TestFlightStats }>('/testflight/stats');
  }

  async testTestFlightConnection(): Promise<ApiResponse<void>> {
    return this.request<ApiResponse<void>>('/testflight/test-connection');
  }

  async sendTestFlightInvites(request: TestFlightInviteRequest): Promise<ApiResponse<{ sent: number }>> {
    return this.request<ApiResponse<{ sent: number }>>('/send-testflight-invites', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Email sending (single or batch)
  async sendEmail(emailData: { to: string; subject: string; body: string; isHtml?: boolean }): Promise<ApiResponse<{emailIds: string[]}> & {emailIds: string[]}> {
    return this.request<ApiResponse<{emailIds: string[]}> & {emailIds: string[]}>('/send-email', {
      method: 'POST',
      body: JSON.stringify(emailData),
    });
  }

  // Batch email sending
  async sendBatchEmails(emails: Array<{to: string; subject: string; body: string; isHtml?: boolean}>): Promise<ApiResponse<{emailIds: string[]}> & {emailIds: string[]}> {
    return this.request<ApiResponse<{emailIds: string[]}> & {emailIds: string[]}>('/send-email', {
      method: 'POST',
      body: JSON.stringify({ emails }),
    });
  }

  // Authentication
  async logout(): Promise<void> {
    await this.request('/logout', {
      method: 'POST',
    });
  }
}

export const apiService = new ApiService();
